import React, { useState, useEffect, useRef, useMemo } from 'react';
import { erpTableRowClasses } from '@euroasiann/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../components/shared/Toast';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { MdDelete, MdDeleteOutline, MdAdd, MdSearch, MdCheckCircle, MdPublic, MdLocationOn, MdClose, MdDownload, MdArrowForward, MdInsertDriveFile, MdFileUpload, MdImage } from 'react-icons/md';
import { downloadRFQPDF } from '../../utils/rfq-pdf';
import { downloadRfqExcelTemplate } from '../../utils/rfq-excel-template';
import { parseRfqExcelFile } from '../../utils/rfq-excel-import';
import { RfqExcelImportPreview } from '../../utils/rfq-excel.types';
import { RfqExcelImportModal } from './components/RfqExcelImportModal';
import { ProcurementActionButton } from '../../components/shared/ProcurementActionButton';
import { SearchableSelect } from '../../components/shared/SearchableSelect';
import { useTheme } from '../../context/ThemeContext';
import { createRfqPageUi } from './create-rfq-theme';
import { RFQ_PAYMENT_TERM_CUSTOM_VALUE, RFQ_TERMS_FIELDS, isPresetRfqPaymentTerm } from '@euroasiann/shared';
import { useDocumentIntelligence, type IdpAutofillPayload } from '@euroasiann/shared/idp';
import { CountryCodeSelect } from '../../components/ui/CountryCodeSelect';
import {
  DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  formatContactPhone,
  hydrateContactPhoneFields,
  normalizeContactPhoneLocal,
} from '../../utils/contact-phone';
import {
  applyRfqImportToForm,
  RFQ_IMPORT_INITIAL_FORM_DATA,
  RFQ_IMPORT_INITIAL_ITEMS,
} from '../../utils/rfq-import-apply';
import { buildCreateRfqImportLookup } from '../../utils/rfq-import-lookup';
import {
  buildRfqImportReviewModel,
  collectUnresolvedImportValues,
} from '../../utils/rfq-import-review';
import { prepareIdpImportWithMasterResolve, buildIdpImportPreview } from '../../utils/rfq-idp-import';
import {
  buildAdminRfqPersistencePayload,
  mergeRfqItemsForHydrate,
} from '../../utils/rfq-build-payload';
import { RFQ_IDP_TARGET_SCHEMA } from '../../utils/rfq-idp-schema';
import {
  appendRfqAttachmentsToFormData,
  attachmentFileKey,
  findDuplicateAttachment,
  mergeImportSourceAttachment,
} from '../../utils/rfq-import-source-attachment';

interface Vessel {
  _id: string;
  name: string;
  imoNumber?: string;
}

interface Port {
  _id: string;
  name: string;
  country: string;
}

interface PortAgent {
  _id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  address?: string;
  workingHours?: string;
}

interface Vendor {
  _id: string;
  name: string;
  isAdminInvited?: boolean;
  onboarding?: {
    country?: string;
    city?: string;
    province?: string;
    port?: string;
    brands?: string[];
    categories?: string[];
    models?: string[];
    logo?: string;
    companyDescription?: string;
  };
}

interface Item {
  id: string;
  impaNo: string;
  itemDescription: string;
  drawingNumber: string;
  partNo: string;
  altPartNo: string;
  modelSerial?: string; // New field
  positionNo: string;
  dimensions: string;
  requiredQuantity: string;
  uom: string;
  quality: string;
  generalRemark: string;
  deliveryPriority: string;
  expectedDeliveryDate?: string;
}

const QUALITY_OPTIONS = [
  'Genuine',
  'OEM',
  'Equivalent',
  'Copy Parts',
  'Mixed',
  'Brand as requested',
  'Custom manufacture',
  'Unknown',
  'Not-Applicable',
  'Compatible',
  'Substitute Brand',
] as const;

interface Brand {
  _id: string;
  name: string;
  status: string;
}

interface Category {
  _id: string;
  name: string;
  status: string;
}

interface Model {
  _id: string;
  name: string;
  brandId?: string;
  status: string;
}

function hydrateRfqDraftForm(savedFormData: Record<string, unknown> | undefined) {
  if (!savedFormData) return INITIAL_FORM_DATA;
  const phoneFields = hydrateContactPhoneFields(savedFormData);
  return {
    ...INITIAL_FORM_DATA,
    ...savedFormData,
    ...phoneFields,
  };
}

const INITIAL_FORM_DATA = RFQ_IMPORT_INITIAL_FORM_DATA;
const INITIAL_ITEMS: Item[] = RFQ_IMPORT_INITIAL_ITEMS;

type RfqSubmitAction = 'draft' | 'sent';

function buildAdminCreateRfqUrl(draftId?: string, search?: URLSearchParams) {
  const params = new URLSearchParams(search ? search.toString() : '');
  if (draftId) params.set('draftId', draftId);
  else params.delete('draftId');
  const qs = params.toString();
  return qs ? `/dashboard/admin/rfqs/new?${qs}` : '/dashboard/admin/rfqs/new';
}

function getAdminRfqsListPath(_search: URLSearchParams) {
  return '/dashboard/admin/purchases?purchasesTab=rfqs';
}

export function CreateRFQPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const search = new URLSearchParams(location.search);
  const draftIdFromQuery = search.get('draftId') || '';
  const pmsJobNoFromQuery = search.get('pmsJobNo') || '';
  const pmsJobIdFromQuery = search.get('pmsJobId') || '';
  const pmsJobNoFromState =
    (location.state as { pmsJobNo?: string; pmsJobId?: string } | null)?.pmsJobNo || '';
  const pmsJobIdFromState =
    (location.state as { pmsJobNo?: string; pmsJobId?: string } | null)?.pmsJobId || '';
  const pmsJobNoRef = (pmsJobNoFromState || pmsJobNoFromQuery).trim();
  const pmsJobIdRef = (pmsJobIdFromState || pmsJobIdFromQuery).trim();
  const vendorIdFromUrl = search.get('vendorId')?.trim() || '';
  const vendorIdsFromUrlKey = (search.get('vendorIds') || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .join(',');
  const adminRfqsListPath = getAdminRfqsListPath(search);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('admin_rfq_draft');
    if (saved) {
      try {
        const { formData: savedFormData } = JSON.parse(saved);
        return hydrateRfqDraftForm(savedFormData);
      } catch (e) {
        return INITIAL_FORM_DATA;
      }
    }
    return INITIAL_FORM_DATA;
  });

  const [paymentTermCustomMode, setPaymentTermCustomMode] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_rfq_draft');
      if (!saved) return false;
      const { formData: savedFormData } = JSON.parse(saved);
      const t = String(savedFormData?.paymentTerm || '');
      return Boolean(t && !isPresetRfqPaymentTerm(t));
    } catch {
      return false;
    }
  });

  const [items, setItems] = useState<Item[]>(() => {
    const saved = localStorage.getItem('admin_rfq_draft');
    if (saved) {
      try {
        const { items: savedItems } = JSON.parse(saved);
        return savedItems || INITIAL_ITEMS;
      } catch (e) {
        return INITIAL_ITEMS;
      }
    }
    return INITIAL_ITEMS;
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  /** Import file held client-side until Apply; never uploaded before RFQ create. */
  const [pendingImportSourceFile, setPendingImportSourceFile] = useState<File | null>(null);
  /** Key of the applied import source in attachments (for UI label / dedupe). */
  const [importSourceAttachmentKey, setImportSourceAttachmentKey] = useState<string | null>(null);
  const [externalInviteEmail, setExternalInviteEmail] = useState('');
  const [externalInviteCompany, setExternalInviteCompany] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdRFQSummary, setCreatedRFQSummary] = useState<any>(null);
  const [savedRfqId, setSavedRfqId] = useState<string | null>(draftIdFromQuery || null);
  const [savedRfqNumber, setSavedRfqNumber] = useState<string | null>(null);
  const [allocatedRfqNumber, setAllocatedRfqNumber] = useState<string>('');
  const [vendorPrefillDone, setVendorPrefillDone] = useState(false);

  const { data: nextRfqNumberData } = useQuery<{ rfqNumber: string }>({
    queryKey: ['admin-rfq-next-number'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/rfq/next-number');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to allocate RFQ number');
      return data.data;
    },
    enabled: !savedRfqId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (nextRfqNumberData?.rfqNumber && !savedRfqNumber) {
      setAllocatedRfqNumber((prev) => prev || nextRfqNumberData.rfqNumber);
    }
  }, [nextRfqNumberData, savedRfqNumber]);

  const [pendingPublishAfterBudget, setPendingPublishAfterBudget] = useState(false);
  const [isBudgetWarningOpen, setIsBudgetWarningOpen] = useState(false);
  const excelInputRef = useRef<HTMLInputElement>(null);
  const [excelImportPreview, setExcelImportPreview] = useState<RfqExcelImportPreview | null>(null);
  const [importUnresolvedValues, setImportUnresolvedValues] = useState<
    Array<{ label: string; value: string; note: string }>
  >([]);
  const [excelImportFileName, setExcelImportFileName] = useState('');
  const [isImportProcessing, setIsImportProcessing] = useState(false);

  const {
    status: idpStatus,
    uploadFile: uploadIdpFile,
  } = useDocumentIntelligence({
    documentType: 'rfq',
    context: 'Admin RFQ creation — extract requisition, equipment, and line items from uploaded documents',
    targetSchema: RFQ_IDP_TARGET_SCHEMA,
    fetchWithAuth: authenticatedFetch,
  });

  // Validation error tracking
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  // Helper: returns red-border + shake CSS when field has an error
  const errClass = (field: string) =>
    fieldErrors.has(field)
      ? ' !border-red-500 rfq-shake'
      : '';

  // Draft Persistence Logic
  useEffect(() => {
    const draft = JSON.stringify({ formData, items });
    localStorage.setItem('admin_rfq_draft', draft);
  }, [formData, items]);

  useEffect(() => {
    if (!pmsJobNoRef) return;
    setFormData(prev => {
      if (prev.requisitionRef?.trim()) return prev;
      return {
        ...prev,
        requisitionRef: pmsJobNoRef,
      };
    });
  }, [pmsJobNoRef]);

  useEffect(() => {
    const ids = [
      ...vendorIdsFromUrlKey.split(',').filter(Boolean),
      ...(vendorIdFromUrl ? [vendorIdFromUrl] : []),
    ].filter((id, idx, arr) => arr.indexOf(id) === idx);
    if (ids.length === 0 || vendorPrefillDone) return;
    setFormData((prev) => ({
      ...prev,
      selectedVendorIds: [...new Set([...prev.selectedVendorIds, ...ids])],
    }));
    setVendorPrefillDone(true);
  }, [vendorIdFromUrl, vendorIdsFromUrlKey, vendorPrefillDone]);

  const { isLoading: isLoadingDraft } = useQuery({
    queryKey: ['admin-rfq-draft', draftIdFromQuery],
    enabled: Boolean(draftIdFromQuery),
    retry: false,
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/v1/admin/rfq/${draftIdFromQuery}`);
      if (!response.ok) {
        throw new Error('Failed to load draft RFQ');
      }
      const body = await response.json();
      const rfq = body.data;
      if (String(rfq?.status || '').toLowerCase() !== 'draft') {
        throw new Error('Only draft RFQs can be edited here');
      }

      const meta = rfq.metadata || {};
      const vesselId = rfq.vesselId?._id || rfq.vesselId;
      setSavedRfqId(String(rfq._id));
      setSavedRfqNumber(rfq.rfqNumber || null);
      setFormData((prev) => ({
        ...prev,
        vesselName: vesselId ? String(vesselId) : rfq.vesselName || prev.vesselName,
        imoNo: rfq.imoNumber || meta.imoNumber || prev.imoNo,
        equipmentTags: rfq.equipmentTags || meta.equipmentTags || prev.equipmentTags,
        category: rfq.category || prev.category,
        subCategory: rfq.subCategory || meta.subCategory || prev.subCategory,
        brand: rfq.brand || prev.brand,
        model: rfq.model || rfq.equipmentModel || prev.model,
        department: rfq.department || meta.department || prev.department,
        remarks: rfq.remarks || meta.remarks || prev.remarks,
        requisitionRef: meta.requisitionRef || meta.pmsJobId || prev.requisitionRef,
        currency: meta.currency || prev.currency,
        quoteDeadline: meta.quoteDeadline || prev.quoteDeadline,
        selectedVendorIds: Array.isArray(rfq.recipientVendorIds)
          ? rfq.recipientVendorIds.map((v: { _id?: string } | string) =>
              String(typeof v === 'object' ? v._id : v)
            )
          : prev.selectedVendorIds,
        contactName: meta.contactName != null ? String(meta.contactName) : prev.contactName,
        contactEmail: meta.contactEmail != null ? String(meta.contactEmail) : prev.contactEmail,
        ...hydrateContactPhoneFields({
          contactPhoneCountryCode: meta.contactPhoneCountryCode,
          contactPhone: meta.contactPhone ?? prev.contactPhone,
        }),
        contactDepartment:
          meta.contactDepartment != null ? String(meta.contactDepartment) : prev.contactDepartment,
        leadDate: meta.leadDate != null ? String(meta.leadDate) : rfq.leadDate || prev.leadDate,
        portId: meta.portId != null ? String(meta.portId) : prev.portId,
        portName: meta.portName != null ? String(meta.portName) : prev.portName,
        portCountry: meta.portCountry != null ? String(meta.portCountry) : prev.portCountry,
        portAgentId: meta.portAgentId != null ? String(meta.portAgentId) : prev.portAgentId,
        portAgentName: meta.portAgentName != null ? String(meta.portAgentName) : prev.portAgentName,
        portAddress: meta.portAddress != null ? String(meta.portAddress) : prev.portAddress,
        portEtaDate: meta.portEtaDate != null ? String(meta.portEtaDate) : prev.portEtaDate,
        deliveryRequiredAt:
          meta.deliveryRequiredAt != null
            ? String(meta.deliveryRequiredAt)
            : prev.deliveryRequiredAt,
        warehouseAddress1:
          meta.warehouseAddress1 != null ? String(meta.warehouseAddress1) : prev.warehouseAddress1,
        warehousePincode:
          meta.warehousePincode != null ? String(meta.warehousePincode) : prev.warehousePincode,
        warehouseCity: meta.warehouseCity != null ? String(meta.warehouseCity) : prev.warehouseCity,
        warehouseState:
          meta.warehouseState != null ? String(meta.warehouseState) : prev.warehouseState,
        warehouseCountry:
          meta.warehouseCountry != null ? String(meta.warehouseCountry) : prev.warehouseCountry,
        requiredCertificates: Array.isArray(meta.requiredCertificates)
          ? meta.requiredCertificates.map(String)
          : prev.requiredCertificates,
        requiredDocuments: Array.isArray(meta.requiredDocuments)
          ? meta.requiredDocuments.map(String)
          : prev.requiredDocuments,
        packingInstructions:
          meta.packingInstructions != null
            ? String(meta.packingInstructions)
            : prev.packingInstructions,
        partialDeliveryAllowed:
          meta.partialDeliveryAllowed != null
            ? String(meta.partialDeliveryAllowed)
            : prev.partialDeliveryAllowed,
        partialQuoteAllowed:
          meta.partialQuoteAllowed != null
            ? String(meta.partialQuoteAllowed)
            : prev.partialQuoteAllowed,
        splitOrderAllowed:
          meta.splitOrderAllowed != null ? String(meta.splitOrderAllowed) : prev.splitOrderAllowed,
        estimatedTotalCost:
          meta.estimatedTotalCost != null
            ? String(meta.estimatedTotalCost)
            : prev.estimatedTotalCost,
        revisionNo: meta.revisionNo != null ? String(meta.revisionNo) : prev.revisionNo,
        quoteValidityDays:
          meta.quoteValidityDays != null ? String(meta.quoteValidityDays) : prev.quoteValidityDays,
        urgencyLevel: meta.urgencyLevel != null ? String(meta.urgencyLevel) : prev.urgencyLevel,
      }));

      const mergedItems = mergeRfqItemsForHydrate(rfq.items, meta.items);
      if (mergedItems.length > 0) {
        setItems(
          mergedItems.map((item) => ({
            id: String(item.id || '1'),
            impaNo: String(item.impaNo || ''),
            itemDescription: String(item.itemDescription || ''),
            drawingNumber: String(item.drawingNumber || ''),
            partNo: String(item.partNo || ''),
            altPartNo: String(item.altPartNo || ''),
            modelSerial: String(item.modelSerial || ''),
            positionNo: String(item.positionNo || ''),
            dimensions: String(item.dimensions || ''),
            requiredQuantity: String(item.requiredQuantity ?? ''),
            uom: String(item.uom || ''),
            quality: String(item.quality || ''),
            generalRemark: String(item.generalRemark || ''),
            deliveryPriority: String(item.deliveryPriority || 'normal'),
            expectedDeliveryDate: String(item.expectedDeliveryDate || ''),
          }))
        );
      }

      localStorage.removeItem('admin_rfq_draft');
      return rfq;
    },
  });

  // Fetch vessels (existing admin vessels API — used by import resolve + payload)
  const { data: vesselsData } = useQuery<Vessel[]>({
    queryKey: ['admin-vessels'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/vessels');
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Fetch vendors
  const { data: vendorsData } = useQuery<Vendor[]>({
    queryKey: ['rfq-vendors'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/rfq/vendors');
      if (!response.ok) throw new Error('Failed to fetch vendors');
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    staleTime: Infinity, // Never consider data stale - prevent all refetching
    gcTime: Infinity, // Keep in cache forever
    refetchInterval: false, // Explicitly disable automatic refetching (false overrides global default)
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on component mount
    refetchOnReconnect: false, // Disable refetch on network reconnect
    retry: false, // Disable retry on failure to prevent continuous retry attempts
  });

  // Fetch categories from Parts Hierarchy (global active only — same source as Product Hierarchy page)
  const { data: categories } = useQuery<Category[]>({
    queryKey: ['admin-product-hierarchy-categories'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/product-hierarchy');
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (!Array.isArray(payload)) return [];
      return payload.map((cat: { _id: string; name: string }) => ({
        _id: String(cat._id),
        name: cat.name,
        status: 'active',
      }));
    },
    retry: false,
  });

  // Fetch brands (only active)
  const { data: brands } = useQuery<Brand[]>({
    queryKey: ['admin-brands', formData.category],
    queryFn: async () => {
      if (!formData.category) return [];
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) return [];
      const response = await authenticatedFetch(`/api/v1/admin/brands?status=active&categoryId=${selectedCategory._id}`);
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    },
    enabled: !!formData.category && !!categories && categories.length > 0,
    staleTime: 0,
    gcTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const rfqVendorLimit = -1;

  const { data: subCategoriesResponse, isLoading: subCategoriesLoading } = useQuery({
    queryKey: ['admin-subcategories', formData.category],
    queryFn: async () => {
      if (!formData.category) return { success: true, data: [] };
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) return { success: true, data: [] };
      const response = await authenticatedFetch(`/api/v1/admin/product-hierarchy/subcategories?categoryId=${selectedCategory._id}`);
      if (!response.ok) throw new Error('Failed to fetch subcategories');
      return response.json();
    },
    enabled: !!formData.category && !!categories?.length,
  });
  const subCategories = subCategoriesResponse?.data || [];

  // Fetch models (only active, filtered by brand)
  const { data: models } = useQuery<Model[]>({
    queryKey: ['admin-models', formData.brand],
    queryFn: async () => {
      if (!formData.brand) return [];
      // Find brand ID from brand name
      const selectedBrand = brands?.find(b => b.name === formData.brand);
      if (!selectedBrand) return [];
      const response = await authenticatedFetch(`/api/v1/admin/models?brandId=${selectedBrand._id}`);
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.data)) return payload.data;
      return [];
    },
    enabled: !!formData.brand && formData.brand !== 'Unknown' && !!brands && brands.length > 0,
    staleTime: Infinity, // Never consider data stale - prevent all refetching
    gcTime: Infinity, // Keep in cache forever
    refetchInterval: false, // Explicitly disable automatic refetching (false overrides global default)
    refetchOnWindowFocus: false, // Disable refetch on window focus for better performance
    refetchOnMount: false, // Disable refetch on component mount
    refetchOnReconnect: false, // Disable refetch on network reconnect
    retry: false, // Disable retry on failure to prevent continuous retry attempts
  });

  // Fetch Ports
  const { data: portsData } = useQuery<Port[]>({
    queryKey: ['ports'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/ports?status=Active&limit=3000');
      const data = await response.json();
      return data.data || [];
    },
    staleTime: Infinity, // Reference data doesn't change
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const portSelectOptions = useMemo(
    () =>
      ((portsData as Port[]) ?? []).map((port) => ({
        value: port._id,
        label: port.name,
        sublabel: port.country,
      })),
    [portsData],
  );

  // Fetch Agents for selected Port
  const { data: agentsData } = useQuery<PortAgent[]>({
    queryKey: ['port-agents', formData.portId],
    queryFn: async () => {
      if (!formData.portId) return [];
      const response = await authenticatedFetch(`/api/v1/ports/${formData.portId}/agents`);
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!formData.portId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Vendor Filtering State
  const [vendorSearch, setVendorSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');

  // Add New Items Modals State
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showModelModal, setShowModelModal] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [newSubCategoryName, setNewSubCategoryName] = useState('');
  const [showPortAgentModal, setShowPortAgentModal] = useState(false);
  const [newPortAgent, setNewPortAgent] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
  });

  const regionsMapping: Record<string, string> = {
    'United Arab Emirates': 'Middle East',
    'Saudi Arabia': 'Middle East',
    'Singapore': 'Asia',
    'India': 'Asia',
    'China': 'Asia',
    'Greece': 'Europe',
    'Norway': 'Europe',
    'Netherlands': 'Europe',
    'USA': 'North America',
    'Panama': 'Central America',
  };

  const regions = ['Asia', 'Europe', 'Middle East', 'North America', 'South America', 'Africa', 'Oceania'];

  // Get unique countries from vendors
  const availableCountries = Array.from(new Set(vendorsData?.map(v => v.onboarding?.country).filter(Boolean) as string[])).sort();

  // Create brand mutation
  const createBrandMutation = useMutation({
    mutationFn: async (name: string) => {
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) {
        throw new Error('Please select a category first');
      }
      const response = await authenticatedFetch('/api/v1/admin/brands', {
        method: 'POST',
        body: JSON.stringify({ name, categoryId: selectedCategory._id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create brand');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-brands', formData.category], (old: Brand[] | undefined) => {
        const list = Array.isArray(old) ? old : [];
        if (list.some((b) => b._id === created?._id || b.name === selectedName)) return list;
        return [...list, created || ({ _id: `temp-${selectedName}`, name: selectedName } as Brand)];
      });
      setFormData((prev) => ({
        ...prev,
        brand: selectedName,
        model: '',
        subCategory: '',
      }));
      setShowBrandModal(false);
      setNewBrandName('');
      queryClient.invalidateQueries({ queryKey: ['admin-brands'] });
      showToast('Brand created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create brand', 'error');
    },
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await authenticatedFetch('/api/v1/admin/categories', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create category');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-product-hierarchy-categories'], (old: Category[] | undefined) => {
        const list = Array.isArray(old) ? old : [];
        if (list.some((c) => c._id === created?._id || c.name === selectedName)) return list;
        return [...list, created || ({ _id: `temp-${selectedName}`, name: selectedName, status: 'active' } as Category)];
      });
      setFormData((prev) => ({
        ...prev,
        category: selectedName,
        brand: '',
        model: '',
        subCategory: '',
      }));
      setShowCategoryModal(false);
      setNewCategoryName('');
      queryClient.invalidateQueries({ queryKey: ['admin-product-hierarchy-categories'] });
      showToast('Category created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create category', 'error');
    },
  });

  // Create subcategory mutation
  const createSubCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const selectedCategory = categories?.find((c: Category) => c.name === formData.category);
      if (!selectedCategory) {
        throw new Error('Please select a category first');
      }
      const response = await authenticatedFetch('/api/v1/admin/product-hierarchy/subcategories', {
        method: 'POST',
        body: JSON.stringify({ name, categoryId: selectedCategory._id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create subcategory');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-subcategories', formData.category], (old: any) => {
        const list = Array.isArray(old?.data) ? old.data : Array.isArray(old) ? old : [];
        if (list.some((s: any) => s._id === created?._id || s.name === selectedName)) {
          return old?.data ? old : { success: true, data: list };
        }
        const next = [...list, created || { _id: `temp-${selectedName}`, name: selectedName }];
        return old?.data !== undefined || old?.success ? { ...(old || {}), success: true, data: next } : { success: true, data: next };
      });
      setFormData((prev) => ({
        ...prev,
        subCategory: selectedName,
      }));
      setShowSubCategoryModal(false);
      setNewSubCategoryName('');
      queryClient.invalidateQueries({ queryKey: ['admin-subcategories'] });
      showToast('SubCategory created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create subcategory', 'error');
    },
  });

  // Create model mutation
  const createModelMutation = useMutation({
    mutationFn: async (name: string) => {
      // Find brand ID from brand name
      const selectedBrand = brands?.find(b => b.name === formData.brand);
      if (!selectedBrand) {
        throw new Error('Please select a brand first');
      }
      const response = await authenticatedFetch('/api/v1/admin/models', {
        method: 'POST',
        body: JSON.stringify({ name, brandId: selectedBrand._id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create model');
      }
      return response.json();
    },
    onSuccess: (res, name) => {
      const created = res?.data;
      const selectedName = created?.name || name;
      queryClient.setQueryData(['admin-models', formData.brand], (old: Model[] | undefined) => {
        const list = Array.isArray(old) ? old : [];
        if (list.some((m) => m._id === created?._id || m.name === selectedName)) return list;
        return [...list, created || ({ _id: `temp-${selectedName}`, name: selectedName } as Model)];
      });
      setFormData((prev) => ({
        ...prev,
        model: selectedName,
        subCategory: '',
      }));
      setShowModelModal(false);
      setNewModelName('');
      queryClient.invalidateQueries({ queryKey: ['admin-models'] });
      showToast('Model created and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create model', 'error');
    },
  });

  const createPortAgentMutation = useMutation({
    mutationFn: async (payload: typeof newPortAgent) => {
      if (!formData.portId) throw new Error('Select a port first');
      const response = await authenticatedFetch(`/api/v1/ports/${formData.portId}/agents`, {
        method: 'POST',
        body: JSON.stringify({
          companyName: payload.companyName.trim(),
          contactPerson: payload.contactPerson.trim(),
          email: payload.email.trim(),
          phone: payload.phone.trim(),
          address: payload.address.trim() || undefined,
          supportedPorts: [formData.portId],
          status: 'Active',
          source: 'global',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create port agent');
      }
      return data;
    },
    onSuccess: (res) => {
      const created = res?.data as PortAgent | undefined;
      if (created?._id) {
        queryClient.setQueryData(['port-agents', formData.portId], (old: PortAgent[] | undefined) => {
          const list = Array.isArray(old) ? old : [];
          if (list.some((a) => a._id === created._id)) return list;
          return [...list, created];
        });
        setFormData((prev) => ({
          ...prev,
          portAgentId: created._id,
          portAgentName: created.companyName || prev.portAgentName,
          portAddress: created.address || prev.portAddress,
        }));
      }
      setShowPortAgentModal(false);
      setNewPortAgent({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
      });
      void queryClient.invalidateQueries({ queryKey: ['port-agents', formData.portId] });
      showToast('Port agent added and selected', 'success');
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to create port agent', 'error');
    },
  });

  const submitRfqMutation = useMutation({
    mutationFn: async ({
      action,
      rfqId,
      payload,
    }: {
      action: RfqSubmitAction;
      rfqId: string | null;
      payload: FormData | Record<string, unknown>;
    }) => {
      const isUpdate = Boolean(rfqId);
      const url = isUpdate ? `/api/v1/admin/rfq/${rfqId}` : '/api/v1/admin/rfq';
      const method = isUpdate ? 'PUT' : 'POST';
      const body =
        payload instanceof FormData
          ? payload
          : JSON.stringify(payload);

      const response = await authenticatedFetch(url, { method, body });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to ${action === 'draft' ? 'save draft' : 'send RFQ'}`);
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-rfqs-all'] }); queryClient.invalidateQueries({ queryKey: ['admin-rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['admin-rfqs-pipeline'] });
      queryClient.invalidateQueries({ queryKey: ['admin-pipeline-overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard-stats'] });

      const rfq = data.data;

      if (variables.action === 'draft') {
        if (rfq?._id) {
          setSavedRfqId(String(rfq._id));
          setSavedRfqNumber(rfq.rfqNumber || null);
          if (String(rfq._id) !== draftIdFromQuery) {
            navigate(buildAdminCreateRfqUrl(String(rfq._id), search), { replace: true });
          }
        }
        showToast(
          rfq?.rfqNumber ? `Draft saved (${rfq.rfqNumber})` : 'Draft saved successfully',
          'success'
        );
        return;
      }

      setCreatedRFQSummary(rfq);
      setShowSuccessModal(true);
      localStorage.removeItem('admin_rfq_draft');
      showToast('RFQ sent to vendors successfully!', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to submit RFQ', 'error');
    },
  });

  const isSubmitting = submitRfqMutation.isPending;

  const handleInputChange = (field: string, value: string | string[]) => {
    // Clear error for this field when user interacts
    setFieldErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });

    setFormData((prev) => {
      const updates: any = { [field]: value };

      // Auto-fill Country based on Port selection
      if (field === 'portId') {
        const selectedPort = (portsData as Port[])?.find(p => p._id === value);
        if (selectedPort) {
          updates.portName = selectedPort.name;
          updates.portCountry = selectedPort.country;
          // Reset Agent when port changes
          updates.portAgentId = '';
          updates.portAgentName = '';
          updates.portAddress = '';
        }
      }

      // Auto-fill Address based on Agent selection
      if (field === 'portAgentId') {
        const selectedAgent = (agentsData as PortAgent[])?.find(a => a._id === value);
        if (selectedAgent) {
          updates.portAgentName = selectedAgent.companyName;
          updates.portAddress = selectedAgent.address || '';
        }
      }

      if (field === 'deliveryRequiredAt') {
        if (value === 'warehouse') {
          updates.portId = '';
          updates.portName = '';
          updates.portCountry = '';
          updates.portAgentId = '';
          updates.portAgentName = '';
          updates.portAddress = '';
          updates.portEtaDate = '';
        } else if (value === 'port') {
          updates.warehouseAddress1 = '';
          updates.warehousePincode = '';
          updates.warehouseCity = '';
          updates.warehouseState = '';
          updates.warehouseCountry = '';
        }
      }

      // Reset dependent fields when parent changes
      if (field === 'category') {
        updates.brand = '';
        updates.model = '';
        updates.subCategory = '';
      }
      if (field === 'brand') {
        updates.model = '';
      }
      return { ...prev, ...updates };
    });

    // Handle "Add New" option (kept separate as it involves side effects like modals)
    if (value === '__add_new_brand__') {
      setShowBrandModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_category__') {
      setShowCategoryModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_sub_category__') {
      setShowSubCategoryModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_model__') {
      setShowModelModal(true);
      setFormData((prev) => ({ ...prev, [field]: '' }));
    } else if (value === '__add_new_port_agent__') {
      if (!formData.portId) {
        showToast('Select a port first', 'warning');
        setFormData((prev) => ({ ...prev, portAgentId: '', portAgentName: '' }));
        return;
      }
      setShowPortAgentModal(true);
      setFormData((prev) => ({ ...prev, portAgentId: '', portAgentName: '' }));
    }
  };

  const handleItemChange = (id: string, field: keyof Item, value: string) => {
    // Clear per-item error on change
    const idx = items.findIndex(i => i.id === id);
    if (idx >= 0) {
      const errKey =
        field === 'itemDescription' ? `item_desc_${idx}` :
          field === 'requiredQuantity' ? `item_qty_${idx}` :
            field === 'uom' ? `item_uom_${idx}` : null;
      if (errKey) {
        setFieldErrors((prev) => {
          if (!prev.has(errKey)) return prev;
          const next = new Set(prev);
          next.delete(errKey);
          return next;
        });
      }
    }
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        impaNo: '',
        itemDescription: '',
        drawingNumber: '',
        partNo: '',
        altPartNo: '',
        positionNo: '',
        dimensions: '',
        requiredQuantity: '',
        uom: 'Pcs',
        quality: '',
        generalRemark: '',
        deliveryPriority: 'normal',
        expectedDeliveryDate: '',
      },
    ]);
  };

  const handleDeleteItem = (id: string) => {
    if (items.length > 1) {
      setItems((prev) => prev.filter((item) => item.id !== id));
    } else {
      showToast('At least one item is required', 'error');
    }
  };

  const getFilteredVendors = () => {
    if (!vendorsData) return [];
    return vendorsData.filter(vendor => {
      const nameMatch = vendor.name.toLowerCase().includes(vendorSearch.toLowerCase());
      const country = vendor.onboarding?.country || '';
      const region = regionsMapping[country] || 'Other';

      const regionMatch = !selectedRegion || region === selectedRegion;
      const countryMatch = !selectedCountry || country === selectedCountry;

      return nameMatch && regionMatch && countryMatch;
    });
  };

  const toggleVendorSelection = (vendorId: string) => {
    setFormData((prev) => {
      const isSelected = prev.selectedVendorIds.includes(vendorId);
      if (isSelected) {
        return {
          ...prev,
          selectedVendorIds: prev.selectedVendorIds.filter((id) => id !== vendorId),
        };
      } else {
        if (rfqVendorLimit !== -1 && prev.selectedVendorIds.length >= rfqVendorLimit) {
          showToast(`Maximum ${rfqVendorLimit} vendors allowed`, 'warning');
          return prev;
        }
        return {
          ...prev,
          selectedVendorIds: [...prev.selectedVendorIds, vendorId],
        };
      }
    });
  };

  const buildRfqPayload = (action: RfqSubmitAction) => {
    const rfqPayload = buildAdminRfqPersistencePayload({
      formData: formData as Record<string, unknown>,
      items,
      action,
      rfqNumber: savedRfqNumber || allocatedRfqNumber || undefined,
      pmsJobId: pmsJobIdRef || undefined,
      vessels: vesselsData ?? [],
    });

    if (savedRfqId) {
      return rfqPayload;
    }

    const formPayload = new FormData();
    Object.entries(rfqPayload).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'object') {
        formPayload.append(key, JSON.stringify(value));
      } else {
        formPayload.append(key, String(value));
      }
    });

    appendRfqAttachmentsToFormData(formPayload, attachments);

    return formPayload;
  };

  const executeSubmit = (action: RfqSubmitAction) => {
    submitRfqMutation.mutate({
      action,
      rfqId: savedRfqId,
      payload: buildRfqPayload(action),
    });
  };

  const validateDraftFields = () => {
    const hasContent =
      Boolean(formData.category?.trim()) ||
      Boolean(formData.category?.trim()) ||
      Boolean(formData.remarks?.trim()) ||
      Boolean(formData.requisitionRef?.trim()) ||
      items.some(
        (item) =>
          item.itemDescription?.trim() ||
          item.partNo?.trim() ||
          item.impaNo?.trim()
      );

    if (!hasContent) {
      showToast('Add at least a category, item, or remarks before saving a draft', 'error');
      return false;
    }
    if (
      (!formData.selectedVendorIds || formData.selectedVendorIds.length === 0) &&
      (!formData.externalVendorInvites || formData.externalVendorInvites.length === 0)
    ) {
      showToast('Select a strategic partner or invite a vendor by email before saving a draft', 'error');
      return false;
    }
    return true;
  };

  const validatePublishFields = () => {
    const errors = new Set<string>();

    if (!formData.quoteDeadline) errors.add('quoteDeadline');
    if (!formData.category) errors.add('category');
    if (!formData.brand) errors.add('brand');
    if (!formData.model) errors.add('model');
    if (
      formData.selectedVendorIds.length === 0 &&
      (!formData.externalVendorInvites || formData.externalVendorInvites.length === 0)
    ) {
      errors.add('vendors');
    }

    items.forEach((item, idx) => {
      if (!item.itemDescription) errors.add(`item_desc_${idx}`);
      if (!item.requiredQuantity) errors.add(`item_qty_${idx}`);
      if (!item.uom) errors.add(`item_uom_${idx}`);
    });

    if (errors.size > 0) {
      setFieldErrors(errors);
      showToast(`Please fill in ${errors.size} required fields before sending`, 'error');
      return false;
    }

    setFieldErrors(new Set());
    return true;
  };

  const runBudgetCheck = () => false;

  const handleSaveDraft = () => {
    if (!validateDraftFields()) return;
    executeSubmit('draft');
  };

  const handleGenerateAndSend = () => {
    if (!validatePublishFields()) return;

    if (runBudgetCheck()) {
      setPendingPublishAfterBudget(true);
      setIsBudgetWarningOpen(true);
      return;
    }

    executeSubmit('sent');
  };

  const handleDownloadExcelTemplate = () => {
    downloadRfqExcelTemplate({
      vessels: (vesselsData ?? []).map((v) => ({
        _id: v._id,
        name: v.name,
        imoNumber: v.imoNumber,
      })),
      categories: categories ?? [],
      vendors: vendorsData ?? [],
      ports: portsData ?? [],
    });
    showToast('Excel template downloaded', 'success');
  };

  const handleApplyExcelImport = () => {
    if (!excelImportPreview || excelImportPreview.errors.length > 0) return;
    const { formData: nextForm, items: nextItems } = applyRfqImportToForm({
      formPatch: excelImportPreview.formPatch,
      items: excelImportPreview.items,
      requisitionRefFallback: pmsJobNoRef,
      initialFormData: INITIAL_FORM_DATA,
      initialItems: INITIAL_ITEMS,
    });
    setFormData(nextForm as typeof formData);
    setItems(nextItems);
    setFieldErrors(new Set());
    setImportUnresolvedValues(
      collectUnresolvedImportValues(buildRfqImportReviewModel(excelImportPreview))
    );
    const fromPdf = excelImportPreview.source === 'pdf';

    // Preserve original import file as RFQ attachment (client-side until create FormData).
    if (pendingImportSourceFile) {
      const merged = mergeImportSourceAttachment(attachments, pendingImportSourceFile);
      setAttachments(merged.attachments);
      if (merged.added || findDuplicateAttachment(merged.attachments, pendingImportSourceFile)) {
        setImportSourceAttachmentKey(attachmentFileKey(pendingImportSourceFile));
      }
      if (!merged.ok && merged.reason) {
        showToast(merged.reason, 'warning');
      }
      setPendingImportSourceFile(null);
    }

    setExcelImportPreview(null);
    setExcelImportFileName('');
    showToast(
      fromPdf
        ? 'RFQ form populated from PDF — review before submitting'
        : 'RFQ form populated from Excel',
      'success'
    );
  };

  const clearPendingImportSession = () => {
    setExcelImportPreview(null);
    setExcelImportFileName('');
    setPendingImportSourceFile(null);
  };

  const applyIdpAutofill = async (payload: IdpAutofillPayload) => {
    const lookup = buildCreateRfqImportLookup({
      categories: categories ?? [],
      vendors: vendorsData ?? [],
      ports: portsData ?? [],
      vessels: (vesselsData ?? []).map((v) => ({
        _id: v._id,
        name: v.name,
        imoNumber: v.imoNumber,
      })),
      fetchWithAuth: authenticatedFetch,
    });

    const prepared = await prepareIdpImportWithMasterResolve(payload, lookup);
    // Shared review modal — do not apply until admin confirms (same as Excel).
    // Keep existing fileName if already set from the file picker.
    setExcelImportFileName((prev) => prev || 'PDF import');
    setExcelImportPreview(buildIdpImportPreview(prepared));
  };

  const handleImportFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;

    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      /\.xlsx?$/i.test(file.name);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

    if (!isExcel && !isPdf) {
      showToast('Please upload an Excel (.xlsx) or PDF file', 'error');
      return;
    }

    if (isExcel) {
      try {
        const preview = await parseRfqExcelFile(
          file,
          buildCreateRfqImportLookup({
            categories: categories ?? [],
            vendors: vendorsData ?? [],
            ports: portsData ?? [],
            vessels: (vesselsData ?? []).map((v) => ({
              _id: v._id,
              name: v.name,
              imoNumber: v.imoNumber,
            })),
            fetchWithAuth: authenticatedFetch,
          })
        );
        setPendingImportSourceFile(file);
        setExcelImportFileName(file.name);
        setExcelImportPreview(preview);
      } catch {
        setPendingImportSourceFile(null);
        showToast('Failed to parse Excel file', 'error');
      }
      return;
    }

    setIsImportProcessing(true);
    try {
      const result = await uploadIdpFile(file);
      if (result) {
        setPendingImportSourceFile(file);
        setExcelImportFileName(file.name);
        await applyIdpAutofill(result);
      } else {
        setPendingImportSourceFile(null);
      }
    } catch {
      setPendingImportSourceFile(null);
      showToast('Failed to extract fields from PDF', 'error');
    } finally {
      setIsImportProcessing(false);
    }
  };

  useEffect(() => {
    if (formData.deliveryRequiredAt !== 'warehouse') return;
    const pincode = formData.warehousePincode.trim();
    if (pincode.length < 4) return;

    const timer = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ postal: pincode });
        if (formData.warehouseCountry.trim()) {
          params.set('country', formData.warehouseCountry.trim());
        }
        const response = await authenticatedFetch(`/api/v1/onboarding/geo/postal?${params.toString()}`);
        if (!response.ok) return;
        const json = await response.json();
        const data = json.data as { city?: string; province?: string; country?: string } | null;
        if (!data) return;
        setFormData((prev) => ({
          ...prev,
          warehouseCity: data.city || prev.warehouseCity,
          warehouseState: data.province || prev.warehouseState,
          warehouseCountry: data.country || prev.warehouseCountry,
        }));
      } catch {
        /* lookup is best-effort */
      }
    }, 450);

    return () => window.clearTimeout(timer);
  }, [formData.deliveryRequiredAt, formData.warehousePincode, formData.warehouseCountry]);

  const isImageAttachment = (file: File) => {
    const type = (file.type || '').toLowerCase();
    return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name);
  };

  const openAttachmentPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    navigate('/dashboard/admin/documents/view', {
      state: {
        blobUrl: url,
        fileName: file.name,
        fileType: file.type || '',
        returnTo: '/dashboard/admin/rfqs/new',
      },
    });
  };

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const ui = useMemo(() => createRfqPageUi(isDark), [isDark]);

  return (
    <div className={ui.page}>
      {/* Shake animation for validation */}
      <style>{`
        @keyframes rfq-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .rfq-shake {
          animation: rfq-shake 0.45s ease;
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 2px rgba(239,68,68,0.25);
        }
      `}</style>
      {/* Breadcrumbs */}
      <div className="mb-3 flex items-center gap-2">
        <span className={cn('text-[10px] font-bold uppercase tracking-[0.2em]', ui.breadcrumbMuted)}>Corporate</span>
        <span className={cn('text-[10px]', ui.breadcrumbSep)}>/</span>
        <span
          className={cn('cursor-pointer text-[10px] font-bold uppercase tracking-[0.2em] transition-colors', ui.breadcrumbLink)}
          onClick={() => navigate(adminRfqsListPath)}
        >
          Sales
        </span>
        <span className={cn('text-[10px]', ui.breadcrumbSep)}>/</span>
        <span className={cn('text-[10px] font-black uppercase tracking-[0.2em]', ui.breadcrumbActive)}>Tactical Deployment</span>
      </div>

      <section className={ui.hero}>
        <div className={ui.heroGradient} />
        <div className={ui.heroGlow} />
        <div className={ui.heroRibbon}>
          <span className={cn('text-[10px] font-bold uppercase tracking-[0.22em]', ui.heroRibbonText)}>
            Procurement Command · RFQ Deployment
          </span>
        </div>
        <div className="relative flex flex-col gap-3 p-4 sm:p-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 space-y-3">
            <h1 className={ui.title}>Create Strategic RFQ</h1>
            <div className="flex flex-wrap items-center gap-3">
              <p className={ui.subtitle}>Sourcing Deployment System</p>
              {savedRfqNumber ? (
                <span className={ui.draftBadge}>Draft • {savedRfqNumber}</span>
              ) : allocatedRfqNumber ? (
                <span className={ui.draftBadge}>RFQ • {allocatedRfqNumber}</span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
            <input
              type="file"
              ref={excelInputRef}
              accept=".xlsx,.xls,.pdf,application/pdf"
              onChange={handleImportFileSelected}
              className="hidden"
            />
            <ProcurementActionButton
              type="button"
              size="md"
              className={cn(ui.cmdBtn, 'min-w-[160px]')}
              onClick={handleDownloadExcelTemplate}
              icon={<MdDownload className="h-4 w-4" />}
            >
              Download Template
            </ProcurementActionButton>
            <ProcurementActionButton
              type="button"
              size="md"
              className={cn(ui.cmdBtn, 'min-w-[160px]')}
              onClick={() => excelInputRef.current?.click()}
              disabled={isImportProcessing || idpStatus === 'uploading' || idpStatus === 'processing'}
              icon={<MdFileUpload className="h-4 w-4" />}
            >
              {isImportProcessing || idpStatus === 'uploading' || idpStatus === 'processing'
                ? 'Extracting…'
                : 'Import Excel / PDF'}
            </ProcurementActionButton>
            <button
              type="button"
              onClick={() => {
                clearPendingImportSession();
                setAttachments([]);
                setImportSourceAttachmentKey(null);
                navigate(adminRfqsListPath);
              }}
              className={cn(ui.cmdBtn, 'min-w-[160px]', ui.abortBtn)}
            >
              Abort Configuration
            </button>
          </div>
        </div>
      </section>

      {excelImportPreview && (
        <RfqExcelImportModal
          preview={excelImportPreview}
          fileName={excelImportFileName}
          onConfirm={handleApplyExcelImport}
          onCancel={clearPendingImportSession}
        />
      )}

      {importUnresolvedValues.length > 0 && (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm"
        >
          <div className="min-w-0">
            <p className="font-semibold text-amber-800">
              Imported values not matched in ERP — select the correct record on the form
            </p>
            <ul className="mt-1 flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-[#0f172a]">
              {importUnresolvedValues.map((v) => (
                <li key={v.label}>
                  <span className="text-[hsl(var(--muted-foreground))]">{v.label}:</span> “{v.value}”
                  {v.note ? <span className="text-[hsl(var(--muted-foreground))]"> · {v.note}</span> : null}
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            onClick={() => setImportUnresolvedValues([])}
            aria-label="Dismiss unmatched import values"
            className="shrink-0 rounded-lg p-1 text-amber-700 hover:bg-amber-100"
          >
            <MdClose className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* RFQ and Vessel Information Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            General Information
          </h2>
        </div>

        {/* Row 1: Core RFQ Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
          <div className="space-y-1">
            <label className={ui.label}>
              RFQ Number
            </label>
            <input
              type="text"
              value="AUTO-GENERATED"
              disabled
              className={ui.fieldDisabled}
            />
            {pmsJobNoRef ? (
              <p className="text-[10px] text-[#0088cc] font-semibold tracking-wide">
                PMS Reference: {pmsJobNoRef}
                {pmsJobIdRef ? ` (ID: ${pmsJobIdRef})` : ''}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Revision
            </label>
            <input
              type="text"
              value={formData.revisionNo}
              onChange={(e) => handleInputChange('revisionNo', e.target.value)}
              placeholder="00"
              className={ui.field}
            />
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className={cn(ui.fieldSelect, errClass('currency'))}
            >
              <option value="">SELECT</option>
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="INR">INR (₹)</option>
              <option value="AED">AED (Dh)</option>
              <option value="SGD">SGD (S$)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Deadline <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.quoteDeadline}
              onChange={(e) => handleInputChange('quoteDeadline', e.target.value)}
              className={cn(ui.field, errClass('quoteDeadline'))}
            />
          </div>
        </div>

        {/* Row 2: Schedule */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="space-y-1">
            <label className={ui.label}>
              Validity
            </label>
            <select
              value={formData.quoteValidityDays}
              onChange={(e) => handleInputChange('quoteValidityDays', e.target.value)}
              className={ui.fieldSelect}
            >
              <option value="15">15 DAYS</option>
              <option value="30">30 DAYS</option>
              <option value="60">60 DAYS</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Urgency
            </label>
            <select
              value={formData.urgencyLevel}
              onChange={(e) => handleInputChange('urgencyLevel', e.target.value)}
              className={cn(
                ui.fieldSelect,
                formData.urgencyLevel === 'Critical' ? ui.urgencyCritical : ui.urgencyNormal
              )}
            >
              <option value="Normal">NORMAL</option>
              <option value="High">HIGH</option>
              <option value="Critical">CRITICAL</option>
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1 flex flex-col">
            <label className={ui.label}>
              Equipment Tags
            </label>
            <input
              type="text"
              value={formData.equipmentTags}
              onChange={(e) => handleInputChange('equipmentTags', e.target.value)}
              placeholder="E.G. MAIN ENGINE, PUMP"
              className={cn(ui.field, 'uppercase')}
            />
          </div>
        </div>
      </div>

      {/* Technical Specification Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Technical Specification
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
          <div className="space-y-1">
            <label className={ui.label}>
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className={cn(ui.fieldSelect, errClass('category'))}
            >
              <option value="">SELECT CATEGORY</option>
              {categories?.map((cat) => (
                <option key={cat._id} value={cat.name}>
                  {cat.name.toUpperCase()}
                </option>
              ))}
              <option value="__add_new_category__" className="text-blue-600 font-bold">
                + ADD NEW CATEGORY
              </option>
            </select>
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Sub Category
            </label>
            <select
              value={formData.subCategory}
              onChange={(e) => handleInputChange('subCategory', e.target.value)}
              disabled={!formData.category}
              className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed', errClass('subCategory'))}
            >
              <option value="">{formData.category ? 'SELECT SUB-TYPE' : 'CHOOSE CATEGORY FIRST'}</option>
              {subCategories?.map((sub: any) => (
                <option key={sub._id} value={sub.name}>
                  {sub.name.toUpperCase()}
                </option>
              ))}
              {formData.category && (
                <option value="__add_new_sub_category__" className="text-blue-600 font-bold">
                  + ADD NEW SUB CATEGORY
                </option>
              )}
            </select>
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Brand <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              disabled={!formData.category}
              className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed', errClass('brand'))}
            >
              <option value="">{formData.category ? 'SELECT BRAND' : 'CHOOSE CATEGORY FIRST'}</option>
              {formData.category && (
                <option value="Unknown">UNKNOWN</option>
              )}
              {brands?.map((brand) => (
                <option key={brand._id} value={brand.name}>
                  {brand.name.toUpperCase()}
                </option>
              ))}
              {formData.category && (
                <option value="__add_new_brand__" className="text-blue-600 font-bold">
                  + ADD NEW BRAND
                </option>
              )}
            </select>
          </div>
          <div className="space-y-1">
            <label className={ui.label}>
              Model <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              disabled={!formData.brand}
              className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed', errClass('model'))}
            >
              <option value="">{formData.brand ? 'SELECT MODEL' : 'CHOOSE BRAND FIRST'}</option>
              {formData.brand && (
                <option value="Unknown">UNKNOWN</option>
              )}
              {models?.map((model) => (
                <option key={model._id} value={model.name}>
                  {model.name.toUpperCase()}
                </option>
              ))}
              {formData.brand && formData.brand !== 'Unknown' && (
                <option value="__add_new_model__" className="text-blue-600 font-bold">
                  + ADD NEW MODEL
                </option>
              )}
            </select>
          </div>
        </div>

        {/* Department & Budget */}
        <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t', ui.divider)}>
          <div className="space-y-1 flex flex-col">
            <label className={ui.label}>
              Department
            </label>
            <select
              value={formData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              className={cn(ui.fieldSelect, errClass('department'))}
            >
              <option value="">SELECT DEPARTMENT</option>
              <option value="engine">ENGINE</option>
              <option value="deck">DECK</option>
              <option value="cabin">CABIN</option>
              <option value="operations">OPERATIONS</option>
              <option value="safety">SAFETY</option>
              <option value="other">OTHER</option>
            </select>
          </div>

          <div className="space-y-1 flex flex-col">
            <label className={ui.label}>
              Estimated Total Cost ({formData.currency || 'USD'})
            </label>
            <input
              type="number"
              value={formData.estimatedTotalCost}
              onChange={(e) => handleInputChange('estimatedTotalCost', e.target.value)}
              placeholder="ENTER ESTIMATED COST"
              className={ui.field}
            />
          </div>
        </div>
      </div>

      {/* Contact Person Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Contact Details
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <label className={ui.label}>Contact Name</label>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => handleInputChange('contactName', e.target.value)}
              placeholder="PRIMARY CONTACT"
              className={cn(ui.field, 'uppercase')}
            />
          </div>
          <div className="space-y-1">
            <label className={ui.label}>Email Address</label>
            <input
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              placeholder="CONTACT@COMPANY.COM"
              className={cn(ui.field, 'uppercase')}
            />
          </div>
          <div className="relative z-10 space-y-1">
            <label className={ui.label} htmlFor="rfq-contact-phone">
              Phone / WhatsApp
            </label>
            <div className="flex gap-2">
              <CountryCodeSelect
                id="rfq-contact-phone-code"
                value={formData.contactPhoneCountryCode}
                onChange={(code) => handleInputChange('contactPhoneCountryCode', code)}
                className="w-36 shrink-0"
                isDark={isDark}
              />
              <input
                id="rfq-contact-phone"
                name="contactPhone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={formData.contactPhone}
                onChange={(e) =>
                  handleInputChange('contactPhone', normalizeContactPhoneLocal(e.target.value))
                }
                placeholder="000 000 0000"
                className={cn(ui.field, 'flex-1 normal-case tabular-nums tracking-normal')}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className={ui.label}>Department</label>
            <select
              value={formData.contactDepartment}
              onChange={(e) => handleInputChange('contactDepartment', e.target.value)}
              className={ui.fieldSelect}
            >
              <option value="Procurement">PROCUREMENT</option>
              <option value="Technical">TECHNICAL</option>
              <option value="Operations">OPERATIONS</option>
            </select>
          </div>
        </div>
      </div>

      {/* Delivery */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>Delivery Required At</h2>
        </div>
        <div className="space-y-3">
          <div className="max-w-md space-y-1">
            <label className={ui.label}>Delivery Location Type</label>
            <select
              value={formData.deliveryRequiredAt}
              onChange={(e) => handleInputChange('deliveryRequiredAt', e.target.value)}
              className={ui.fieldSelect}
            >
              <option value="port">PORT</option>
              <option value="warehouse">WAREHOUSE</option>
            </select>
          </div>

          {formData.deliveryRequiredAt === 'port' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className={ui.label}>Port Name</label>
                <SearchableSelect
                  value={formData.portId}
                  onChange={(value) => handleInputChange('portId', value)}
                  options={portSelectOptions}
                  placeholder="SELECT PORT"
                  allLabel="SELECT PORT"
                  searchPlaceholder="Search ports by name or country..."
                  buttonClassName={ui.fieldSelect}
                />
              </div>
              <div className="space-y-1">
                <label className={ui.label}>Port Agent</label>
                <select
                  value={formData.portAgentId}
                  onChange={(e) => handleInputChange('portAgentId', e.target.value)}
                  disabled={!formData.portId}
                  className={cn(ui.fieldSelect, 'disabled:opacity-50 disabled:cursor-not-allowed')}
                >
                  <option value="">SELECT AGENT</option>
                  {formData.portId && (
                    <option value="__add_new_port_agent__" className="text-blue-600 font-bold">
                      + ADD PORT AGENT
                    </option>
                  )}
                  {(agentsData as PortAgent[])?.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.companyName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className={ui.label}>Delivery Address / Terminal</label>
                <textarea
                  value={formData.portAddress}
                  onChange={(e) => handleInputChange('portAddress', e.target.value)}
                  placeholder="FULL ADDRESS, TERMINAL NAME, BERTH NO..."
                  rows={2}
                  className={cn(ui.field, 'uppercase resize-none')}
                />
              </div>
              <div className="space-y-1">
                <label className={ui.label}>Expected ETA</label>
                <input
                  type="date"
                  value={formData.portEtaDate}
                  onChange={(e) => handleInputChange('portEtaDate', e.target.value)}
                  className={ui.field}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 space-y-1">
                <label className={ui.label}>Address Line 1</label>
                <input
                  type="text"
                  value={formData.warehouseAddress1}
                  onChange={(e) => handleInputChange('warehouseAddress1', e.target.value)}
                  placeholder="STREET, BUILDING, WAREHOUSE NAME"
                  className={cn(ui.field, 'uppercase')}
                />
              </div>
              <div className="space-y-1">
                <label className={ui.label}>Pincode / Postal Code</label>
                <input
                  type="text"
                  value={formData.warehousePincode}
                  onChange={(e) => handleInputChange('warehousePincode', e.target.value)}
                  placeholder="ENTER PINCODE"
                  className={ui.field}
                />
              </div>
              <div className="space-y-1">
                <label className={ui.label}>City</label>
                <input
                  type="text"
                  value={formData.warehouseCity}
                  onChange={(e) => handleInputChange('warehouseCity', e.target.value)}
                  placeholder="AUTO-FILLED FROM PINCODE"
                  className={ui.field}
                />
              </div>
              <div className="space-y-1">
                <label className={ui.label}>State / Province</label>
                <input
                  type="text"
                  value={formData.warehouseState}
                  onChange={(e) => handleInputChange('warehouseState', e.target.value)}
                  placeholder="AUTO-FILLED FROM PINCODE"
                  className={ui.field}
                />
              </div>
              <div className="space-y-1">
                <label className={ui.label}>Country</label>
                <input
                  type="text"
                  value={formData.warehouseCountry}
                  onChange={(e) => handleInputChange('warehouseCountry', e.target.value)}
                  placeholder="AUTO-FILLED FROM PINCODE"
                  className={ui.field}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Attachments Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Supporting Documents
          </h2>
        </div>

        <div className={ui.dropzone}>
          <div className="mx-auto max-w-xs space-y-2.5">
            <div className={ui.dropzoneIcon}>
              <MdFileUpload className={cn('h-7 w-7', isDark ? 'text-[#38bdf8]' : 'text-[#0088cc]')} />
            </div>
            <div className="space-y-1">
              <p className={cn('text-sm font-bold uppercase tracking-tight', ui.dropzoneTitle)}>Drop files or click to upload</p>
              <p className={cn('text-[11px] font-bold uppercase tracking-widest', ui.dropzoneHint)}>
                PDF, images, DOCX, XLSX (MAX 10MB)
              </p>
            </div>
            <label className="cursor-pointer inline-block">
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,image/*,application/pdf"
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setAttachments((prev) => [...prev, ...newFiles]);
                  if (e.target) e.target.value = '';
                }}
                className="hidden"
              />
              <span className="rounded-lg border border-[#0088cc]/40 bg-[#0088cc] px-8 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white shadow-md transition-all hover:bg-[#0077b3]">
                Choose Files
              </span>
            </label>
          </div>
        </div>

        <p className={cn('mt-4 text-center text-[11px] font-bold uppercase tracking-widest', ui.dropzoneHint)}>
          Use Import Excel / PDF above to auto-fill fields via OCR extraction
        </p>

        {/* Selected Files List */}
        {attachments.length > 0 && (
          <div className="mt-4 space-y-2">
            <h3 className={cn('text-[10px] font-black uppercase tracking-[0.2em] mb-2', ui.label)}>
              Selected for Upload ({attachments.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {attachments.map((file, idx) => (
                <div key={`${file.name}-${file.size}-${idx}`} className={ui.attachmentCard}>
                  <div className="flex min-w-0 items-center gap-4">
                    <div className={ui.attachmentIcon}>
                      {isImageAttachment(file) ? (
                        <MdImage className="h-5 w-5" />
                      ) : (
                        <MdInsertDriveFile className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p
                        className={cn('truncate text-[13px] font-bold uppercase tracking-tight', ui.attachmentName)}
                        title={file.name}
                      >
                        {file.name}
                      </p>
                      <p className={cn('text-[10px] font-bold uppercase', ui.dropzoneHint)}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                        {importSourceAttachmentKey === attachmentFileKey(file) ? ' · Import source' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openAttachmentPreview(file)}
                      className="rounded-xl border border-[#0088cc]/40 bg-[#0088cc] px-3.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-white shadow-sm transition-all hover:bg-[#0077b3] hover:shadow-md"
                      title="View file"
                      aria-label={`View ${file.name}`}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const key = attachmentFileKey(file);
                        setAttachments((prev) => prev.filter((_, i) => i !== idx));
                        setImportSourceAttachmentKey((prev) => (prev === key ? null : prev));
                      }}
                      className={ui.deleteRowBtn}
                      title="Remove File"
                      aria-label={`Remove ${file.name}`}
                    >
                      <MdDelete className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={ui.vendorPanel(fieldErrors.has('vendors'))}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#0088cc] rounded-xl shadow-lg shadow-[#0088cc]/20">
                <MdPublic className="w-5 h-5 text-white" />
              </div>
              <h2 className={ui.vendorTitle}>Strategic Partners</h2>
            </div>
            <p className={cn('text-sm font-medium', ui.vendorSubtitle)}>Select up to {rfqVendorLimit === -1 ? 'unlimited' : rfqVendorLimit} specialized vendors for your global quotation</p>
            {fieldErrors.has('vendors') && (
              <p className="text-sm text-red-500 font-semibold mt-1">⚠ Select a strategic partner or invite a vendor by email</p>
            )}
          </div>
          <div className={cn(
            'px-6 py-3 rounded-2xl border',
            isDark ? 'bg-[#0088cc]/20 border-[#0088cc]/50' : 'bg-blue-50 border-[#0088cc]/25'
          )}>
            <span className="text-sm font-black text-[#0088cc] tracking-wider">
              {formData.selectedVendorIds.length} / {rfqVendorLimit === -1 ? '∞' : rfqVendorLimit} SELECTED
            </span>
          </div>
        </div>

        {/* Premium Filters Bar */}
        <div className={ui.vendorFilters}>
          <div className="relative min-w-[300px] flex-1 group">
            <MdSearch className={cn('absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors', ui.searchIcon)} />
            <input
              type="text"
              placeholder="SEARCH VENDORS..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              className={ui.vendorSearch}
            />
          </div>
          <div className="w-48">
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className={ui.vendorSelect}
            >
              <option value="">ALL REGIONS</option>
              {regions.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="w-48">
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className={ui.vendorSelect}
            >
              <option value="">ALL COUNTRIES</option>
              {availableCountries.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </div>
        </div>

        {/* Refined Vendor Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
          {getFilteredVendors().map(vendor => {
            const isSelected = formData.selectedVendorIds.includes(vendor._id);
            return (
              <div
                key={vendor._id}
                onClick={() => toggleVendorSelection(vendor._id)}
                className={ui.vendorCard(isSelected)}
              >
                <div className={ui.vendorLogoWrap(isSelected)}>
                  {vendor.onboarding?.logo ? (
                    <img src={vendor.onboarding.logo} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <MdPublic className={cn('h-6 w-6', ui.vendorIcon(isSelected))} />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <h4 className={ui.vendorName(isSelected)}>
                    {vendor.name.toUpperCase()}
                  </h4>
                  <div className="flex items-center gap-1.5">
                    <MdLocationOn className={cn('h-3.5 w-3.5', ui.locationIcon)} />
                    <span className={ui.label}>
                      {vendor.onboarding?.country || 'GLOBAL NETWORK'}
                    </span>
                  </div>
                </div>

                <div className={ui.vendorCheck(isSelected)}>
                  {isSelected && <MdCheckCircle className="w-4 h-4 text-white" />}
                </div>
              </div>
            );
          })}

          {getFilteredVendors().length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <div className={ui.vendorEmptyIcon}>
                <MdSearch size={48} className={ui.locationIcon} />
              </div>
              <p className={cn('mb-2 text-2xl font-bold', ui.vendorEmptyTitle)}>No matches found</p>
              <p className={cn('mx-auto max-w-xs text-sm', ui.vendorEmptyText)}>Try adjusting your filters to find suitable strategic partners</p>
            </div>
          )}
        </div>

        {/* Invite unregistered vendors by email */}
        <div className="mt-6 rounded-xl border border-[#0088cc]/20 bg-[#0088cc]/5 p-4 dark:border-[#0088cc]/30 dark:bg-[#0088cc]/10">
          <h3 className={cn('mb-1 text-[11px] font-black uppercase tracking-[0.18em]', ui.label)}>
            Invite by email (not on platform yet)
          </h3>
          <p className={cn('mb-3 text-xs', ui.vendorSubtitle)}>
            They receive a secure guest quote link, plus CTAs to join as a contracted vendor and unlock Vendor ERP ($20/seat via Razorpay).
          </p>
          <div className="flex flex-col gap-2 md:flex-row md:items-end">
            <div className="flex-1">
              <label className={cn('mb-1 block text-[10px] font-bold uppercase', ui.label)}>Email *</label>
              <input
                type="email"
                value={externalInviteEmail}
                onChange={(e) => setExternalInviteEmail(e.target.value)}
                placeholder="vendor@company.com"
                className={ui.field}
              />
            </div>
            <div className="flex-1">
              <label className={cn('mb-1 block text-[10px] font-bold uppercase', ui.label)}>Company (optional)</label>
              <input
                type="text"
                value={externalInviteCompany}
                onChange={(e) => setExternalInviteCompany(e.target.value)}
                placeholder="Company name"
                className={ui.field}
              />
            </div>
            <button
              type="button"
              className={cn(ui.cmdBtn, 'min-w-[120px]')}
              onClick={() => {
                const email = externalInviteEmail.trim().toLowerCase();
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                  showToast('Enter a valid vendor email', 'error');
                  return;
                }
                setFormData((prev) => {
                  const existing = Array.isArray(prev.externalVendorInvites)
                    ? prev.externalVendorInvites
                    : [];
                  if (existing.some((i) => i.email === email)) {
                    showToast('This email is already invited', 'warning');
                    return prev;
                  }
                  return {
                    ...prev,
                    externalVendorInvites: [
                      ...existing,
                      {
                        email,
                        companyName: externalInviteCompany.trim() || undefined,
                      },
                    ],
                  };
                });
                setFieldErrors((prev) => {
                  if (!prev.has('vendors')) return prev;
                  const next = new Set(prev);
                  next.delete('vendors');
                  return next;
                });
                setExternalInviteEmail('');
                setExternalInviteCompany('');
              }}
            >
              Add invite
            </button>
          </div>
          {(formData.externalVendorInvites?.length ?? 0) > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {formData.externalVendorInvites.map((invite) => (
                <span
                  key={invite.email}
                  className="inline-flex items-center gap-2 rounded-full border border-[#0088cc]/30 bg-white px-3 py-1 text-xs font-semibold text-[hsl(var(--foreground))] dark:bg-[#0b1e2a]"
                >
                  {invite.companyName ? `${invite.companyName} · ` : ''}
                  {invite.email}
                  <button
                    type="button"
                    className="text-red-500"
                    title="Remove invite"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        externalVendorInvites: (prev.externalVendorInvites || []).filter(
                          (i) => i.email !== invite.email
                        ),
                      }))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer Stats bar */}
        <div className={ui.vendorFooter}>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-3">
              {getFilteredVendors().slice(0, 5).map((v) => (
                <div key={v._id} className={ui.vendorAvatar}>
                  {v.name.charAt(0)}
                </div>
              ))}
              {getFilteredVendors().length > 5 && (
                <div className={cn(ui.vendorAvatar, 'border-[#0088cc] bg-[#0088cc] text-white')}>
                  +{getFilteredVendors().length - 5}
                </div>
              )}
            </div>
            <p className={cn('text-xs font-black uppercase tracking-[0.2em]', ui.dropzoneHint)}>
              Trusted by enterprise leaders
            </p>
          </div>
        </div>
      </div>

      {/* Items Section */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>
            Required Items
          </h2>
        </div>
        <div className="min-w-0 max-w-full overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1450px]">
            <thead>
              <tr className={ui.tableHead}>
                <th className="px-3 py-2 w-12 text-center">#</th>
                <th className="px-2 py-2 w-28">IMPA</th>
                <th className="px-2 py-2 flex-1">Description / Specification</th>
                <th className="px-2 py-2 w-36">Part No</th>
                <th className="px-2 py-2 w-32">Drawing No</th>
                <th className="px-2 py-2 w-32">Dim.</th>
                <th className="px-2 py-2 w-24">Qty</th>
                <th className="px-2 py-2 w-24">UoM</th>
                <th className="px-2 py-2 w-32">Quality</th>
                <th className="px-2 py-2 w-28">Priority</th>
                <th className="px-2 py-2 w-48">Remarks</th>
                <th className="px-3 py-2 w-16 text-center">Action</th>
              </tr>
            </thead>
            <tbody className={ui.tableBody}>
              {items.map((item, index) => (
                <tr key={item.id} className={erpTableRowClasses()}>
                  <td className={cn('px-3 py-2 text-center text-[12px] font-bold', ui.tableRowNum)}>
                    {index + 1}
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={item.impaNo}
                      onChange={(e) => handleItemChange(item.id, 'impaNo', e.target.value)}
                      className={ui.tableField}
                      placeholder="IMPA"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <textarea
                      value={item.itemDescription}
                      onChange={(e) => handleItemChange(item.id, 'itemDescription', e.target.value)}
                      className={cn(ui.tableFieldTextarea, 'uppercase', errClass(`item_desc_${index}`))}
                      placeholder="DESCRIPTION *"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col gap-1">
                      <input
                        type="text"
                        value={item.partNo}
                        onChange={(e) => handleItemChange(item.id, 'partNo', e.target.value)}
                        className={ui.tableField}
                        placeholder="PART NO"
                      />
                      <input
                        type="text"
                        value={item.altPartNo}
                        onChange={(e) => handleItemChange(item.id, 'altPartNo', e.target.value)}
                        className={cn(ui.tableField, 'py-1 text-[11px] normal-case tracking-normal')}
                        placeholder="alt part no"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={item.drawingNumber}
                      onChange={(e) => handleItemChange(item.id, 'drawingNumber', e.target.value)}
                      className={ui.tableField}
                      placeholder="DRAWING NO"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={item.dimensions}
                      onChange={(e) => handleItemChange(item.id, 'dimensions', e.target.value)}
                      className={ui.tableField}
                      placeholder="DIMENSIONS"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      value={item.requiredQuantity}
                      onChange={(e) => handleItemChange(item.id, 'requiredQuantity', e.target.value)}
                      className={cn(ui.tableFieldCenter, errClass(`item_qty_${index}`))}
                      min="1"
                      placeholder="QTY"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={item.uom}
                      onChange={(e) => handleItemChange(item.id, 'uom', e.target.value)}
                      className={cn(ui.tableSelect, errClass(`item_uom_${index}`))}
                    >
                      <option value="">UOM</option>
                      <option value="Pcs">PCS</option>
                      <option value="Kgs">KGS</option>
                      <option value="Ltrs">LTRS</option>
                      <option value="Mtrs">MTRS</option>
                      <option value="Sets">SETS</option>
                      <option value="Box">BOX</option>
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={item.quality}
                      onChange={(e) => handleItemChange(item.id, 'quality', e.target.value)}
                      className={ui.tableSelect}
                    >
                      <option value="">QUALITY</option>
                      {QUALITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={item.deliveryPriority}
                      onChange={(e) => handleItemChange(item.id, 'deliveryPriority', e.target.value)}
                      className={ui.tableSelect}
                    >
                      <option value="normal">NORMAL</option>
                      <option value="urgent">URGENT</option>
                      <option value="critical">CRITICAL</option>
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <textarea
                      value={item.generalRemark}
                      onChange={(e) => handleItemChange(item.id, 'generalRemark', e.target.value)}
                      className={cn(ui.tableFieldTextarea, 'uppercase', isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]')}
                      placeholder="REMARKS"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => setItems(items.filter((i) => i.id !== item.id))}
                      className={ui.deleteRowBtn}
                    >
                      <MdDeleteOutline className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={ui.itemsFooter}>
          <p className={cn('text-[12px] font-bold uppercase tracking-widest', ui.itemsFooterText)}>
            {items.length} {items.length === 1 ? 'ITEM' : 'ITEMS'} IN RFQ MANIFEST
          </p>
          <button
            onClick={handleAddItem}
            className={ui.addItemBtn}
          >
            + Add New Item
          </button>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className={ui.section}>
        <div className={ui.sectionHeader}>
          <h2 className={ui.sectionTitle}>Terms & Conditions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {RFQ_TERMS_FIELDS.map(({ key, label, options }) => {
            if (key === 'paymentTerm') {
              const current = String(formData.paymentTerm || '');
              const selectVal = paymentTermCustomMode
                ? RFQ_PAYMENT_TERM_CUSTOM_VALUE
                : current && isPresetRfqPaymentTerm(current)
                  ? current
                  : current
                    ? RFQ_PAYMENT_TERM_CUSTOM_VALUE
                    : '';
              return (
                <div key={key} className="space-y-1">
                  <label className={ui.label}>{label}</label>
                  <select
                    value={selectVal}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === RFQ_PAYMENT_TERM_CUSTOM_VALUE) {
                        setPaymentTermCustomMode(true);
                        if (isPresetRfqPaymentTerm(current)) handleInputChange(key, '');
                      } else {
                        setPaymentTermCustomMode(false);
                        handleInputChange(key, next);
                      }
                    }}
                    className={ui.fieldSelect}
                  >
                    <option value="">SELECT {label.toUpperCase()}</option>
                    {options.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                    <option value={RFQ_PAYMENT_TERM_CUSTOM_VALUE}>Custom (enter your own)</option>
                  </select>
                  {selectVal === RFQ_PAYMENT_TERM_CUSTOM_VALUE && (
                    <input
                      type="text"
                      value={current}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      placeholder="e.g. 40% advance, 60% against B/L"
                      className={ui.field}
                      autoFocus
                    />
                  )}
                </div>
              );
            }
            return (
              <div key={key} className="space-y-1">
                <label className={ui.label}>{label}</label>
                <select
                  value={formData[key as keyof typeof formData] as string}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  className={ui.fieldSelect}
                >
                  <option value="">SELECT {label.toUpperCase()}</option>
                  {options.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Save Draft / Generate & Send — clear floating support chat (bottom-right FAB) */}
      <div className="mt-8 flex flex-col-reverse gap-2 pb-8 pr-20 sm:flex-row sm:justify-end sm:gap-3 lg:pr-24">
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={isSubmitting || isLoadingDraft}
          className={cn(ui.cmdBtn, 'min-w-[160px] disabled:opacity-50', ui.saveDraftBtn)}
        >
          {isSubmitting ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          type="button"
          onClick={handleGenerateAndSend}
          disabled={isSubmitting || isLoadingDraft}
          className={cn(ui.cmdBtn, 'min-w-[200px] disabled:opacity-50', ui.sendBtn)}
        >
          {isSubmitting ? 'Sending RFQ...' : 'Generate & Send RFQ'}
        </button>
      </div>

      {/* Add Port Agent Modal */}
      {showPortAgentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[1100] p-4">
          <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">
              Add Port Agent
            </h3>
            <div className="space-y-3 mb-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))]">Company name *</label>
                <input
                  type="text"
                  value={newPortAgent.companyName}
                  onChange={(e) => setNewPortAgent((p) => ({ ...p, companyName: e.target.value }))}
                  placeholder="Company name"
                  autoFocus
                  className="w-full px-4 py-2 border border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))]">Contact person *</label>
                <input
                  type="text"
                  value={newPortAgent.contactPerson}
                  onChange={(e) => setNewPortAgent((p) => ({ ...p, contactPerson: e.target.value }))}
                  placeholder="Contact person"
                  className="w-full px-4 py-2 border border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))]">Email *</label>
                <input
                  type="email"
                  value={newPortAgent.email}
                  onChange={(e) => setNewPortAgent((p) => ({ ...p, email: e.target.value }))}
                  placeholder="Email"
                  className="w-full px-4 py-2 border border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))]">Phone number *</label>
                <input
                  type="text"
                  value={newPortAgent.phone}
                  onChange={(e) => setNewPortAgent((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Phone number"
                  className="w-full px-4 py-2 border border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))]">Address (optional)</label>
                <textarea
                  value={newPortAgent.address}
                  onChange={(e) => setNewPortAgent((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Address"
                  rows={2}
                  className="w-full px-4 py-2 border border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowPortAgentModal(false);
                  setNewPortAgent({
                    companyName: '',
                    contactPerson: '',
                    email: '',
                    phone: '',
                    address: '',
                  });
                }}
                className="px-4 py-2 border border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/65 hover:bg-[#0088cc]/5"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !newPortAgent.companyName.trim() ||
                  !newPortAgent.contactPerson.trim() ||
                  !newPortAgent.email.trim() ||
                  !newPortAgent.phone.trim() ||
                  createPortAgentMutation.isPending
                }
                onClick={() => createPortAgentMutation.mutate(newPortAgent)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createPortAgentMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Brand Modal */}
      {
        showBrandModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Brand</h3>
              <input
                type="text"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newBrandName.trim() && !createBrandMutation.isPending) {
                    e.preventDefault();
                    createBrandMutation.mutate(newBrandName.trim());
                  }
                }}
                placeholder="Enter brand name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowBrandModal(false);
                    setNewBrandName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newBrandName.trim()) {
                      createBrandMutation.mutate(newBrandName.trim());
                    }
                  }}
                  disabled={!newBrandName.trim() || createBrandMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createBrandMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Category Modal */}
      {
        showCategoryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Category</h3>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCategoryName.trim() && !createCategoryMutation.isPending) {
                    e.preventDefault();
                    createCategoryMutation.mutate(newCategoryName.trim());
                  }
                }}
                placeholder="Enter category name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setNewCategoryName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newCategoryName.trim()) {
                      createCategoryMutation.mutate(newCategoryName.trim());
                    }
                  }}
                  disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createCategoryMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Sub Category Modal */}
      {
        showSubCategoryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Sub Category</h3>
              <input
                type="text"
                value={newSubCategoryName}
                onChange={(e) => setNewSubCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    newSubCategoryName.trim() &&
                    formData.category &&
                    !createSubCategoryMutation.isPending
                  ) {
                    e.preventDefault();
                    createSubCategoryMutation.mutate(newSubCategoryName.trim());
                  }
                }}
                placeholder="Enter sub category name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              {!formData.category && (
                <p className="text-sm text-red-500 mb-4">Please select a category first.</p>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowSubCategoryModal(false);
                    setNewSubCategoryName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newSubCategoryName.trim()) {
                      createSubCategoryMutation.mutate(newSubCategoryName.trim());
                    }
                  }}
                  disabled={!newSubCategoryName.trim() || !formData.category || createSubCategoryMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createSubCategoryMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Model Modal */}
      {
        showModelModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
            <div className="bg-white dark:bg-[#0088cc]/18 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] dark:text-white mb-4">Add New Model</h3>
              <input
                type="text"
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newModelName.trim() && !createModelMutation.isPending) {
                    e.preventDefault();
                    createModelMutation.mutate(newModelName.trim());
                  }
                }}
                placeholder="Enter model name"
                autoFocus
                className="w-full px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg bg-white dark:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowModelModal(false);
                    setNewModelName('');
                  }}
                  className="px-4 py-2 border border-[#0088cc]/25 dark:border-[#0088cc]/25 rounded-lg text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/20"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newModelName.trim()) {
                      createModelMutation.mutate(newModelName.trim());
                    }
                  }}
                  disabled={!newModelName.trim() || createModelMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createModelMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Success Modal */}
      {
        showSuccessModal && createdRFQSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white dark:bg-[#0088cc]/25 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-300">
              <div className="bg-blue-600 p-8 text-center text-white">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MdCheckCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">RFQ Sent Successfully!</h3>
                <p className="text-blue-100 opacity-90">Your request has been published and sent to selected vendors.</p>
              </div>

              <div className="p-8">
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center pb-3 border-b border-[#0088cc]/15 dark:border-[#0088cc]/20">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">RFQ Number</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{createdRFQSummary.rfqNumber}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#0088cc]/15 dark:border-[#0088cc]/20">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Category</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{formData.category || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#0088cc]/15 dark:border-[#0088cc]/20">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Items</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{items.length} items</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Recipients</span>
                    <span className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{formData.selectedVendorIds.length} Vendors</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={async () => {
                      const agentInfo = (agentsData as PortAgent[])?.find(a => a._id === formData.portAgentId);
                      const { fetchMyBranding } = await import('../../utils/document-letterhead');
                      const { authenticatedFetch } = await import('../../lib/api');
                      const branding = await fetchMyBranding('/api/v1/admin', authenticatedFetch);
                      
                      await downloadRFQPDF({
                        rfqNumber: createdRFQSummary?.rfqNumber || 'DRAFT',
                        revisionNo: formData.revisionNo,
                        createdAt: new Date().toISOString(),
                        quoteDeadline: formData.quoteDeadline,
                        quoteValidityDays: formData.quoteValidityDays,
                        urgencyLevel: formData.urgencyLevel,
                        requisitionRef: formData.requisitionRef,
                        currency: formData.currency,
                        rfqStatus: 'sent',
                        department: formData.department,
                        estimatedTotalCost: formData.estimatedTotalCost,
                        
                        customerName: branding.name || 'Customer',
                        companyNameForLetterhead: branding.name || 'Customer',
                        companyLogoUrl: branding.logoUrl,
                        
                        brand: formData.brand,
                        model: formData.model,
                        category: formData.category,
                        subCategory: formData.subCategory,
                        equipmentTags: formData.equipmentTags,
                        
                        deliveryRequiredAt: formData.deliveryRequiredAt,
                        deliveryType: formData.deliveryType,
                        supplyPort: formData.deliveryRequiredAt === 'port' ? formData.portName : undefined,
                        portCountry: formData.deliveryRequiredAt === 'port' ? formData.portCountry : undefined,
                        portEtaDate: formData.deliveryRequiredAt === 'port' ? formData.portEtaDate : undefined,
                        portAgentName: formData.deliveryRequiredAt === 'port' ? (formData.portAgentName || agentInfo?.companyName) : undefined,
                        portAgentPhone: formData.deliveryRequiredAt === 'port' ? agentInfo?.phone : undefined,
                        portAgentEmail: formData.deliveryRequiredAt === 'port' ? agentInfo?.email : undefined,
                        portAddress:
                          formData.deliveryRequiredAt === 'port'
                            ? formData.portAddress || agentInfo?.address
                            : formData.deliveryRequiredAt === 'warehouse'
                              ? [formData.warehouseAddress1, formData.warehouseCity, formData.warehouseState, formData.warehousePincode, formData.warehouseCountry]
                                  .filter(Boolean)
                                  .join(', ')
                              : undefined,

                        leadDate: formData.leadDate,
                        creditType: formData.creditType,
                        paymentTerm: formData.paymentTerm,
                        insuranceTerm: formData.insuranceTerm,
                        taxTerm: formData.taxTerm,
                        transportTerm: formData.transportTerm,
                        deliveryTerm: formData.deliveryTerm,
                        packingTerm: formData.packingTerm,
                        partialQuoteAllowed: formData.partialQuoteAllowed,
                        splitOrderAllowed: formData.splitOrderAllowed,
                        
                        contactName: formData.contactName,
                        contactEmail: formData.contactEmail,
                        contactPhone: formatContactPhone(formData.contactPhoneCountryCode, formData.contactPhone),
                        contactDepartment: formData.contactDepartment,
                        
                        requiredPaymentTerms: formData.requiredPaymentTerms,
                        requiredCertificates: formData.requiredCertificates,
                        requiredDocuments: formData.requiredDocuments,
                        packingInstructions: formData.packingInstructions,
                        partialDeliveryAllowed: formData.partialDeliveryAllowed,

                        remarks: formData.remarks,
                        items: items.map(item => ({
                          positionNo: item.positionNo,
                          impaNo: item.impaNo,
                          description: item.itemDescription,
                          drawingNumber: item.drawingNumber,
                          partNo: item.partNo,
                          altPartNo: item.altPartNo,
                          modelSerial: item.modelSerial,
                          dimensions: item.dimensions,
                          requiredQty: item.requiredQuantity,
                          uom: item.uom,
                          quality: item.quality,
                          remark: item.generalRemark,
                          deliveryPriority: item.deliveryPriority,
                        }))
                      });
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0088cc]/20 dark:border-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] rounded-xl hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/18 transition-all font-semibold"
                  >
                    <MdDownload className="w-5 h-5" />
                    Download
                  </button>
                  <button
                    onClick={() => navigate(adminRfqsListPath)}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold shadow-lg shadow-blue-500/20"
                  >
                    Done
                    <MdArrowForward className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Budget Warning Modal */}
      {
        isBudgetWarningOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-[#0088cc]/20 animate-in fade-in zoom-in duration-200">
              <div className="p-6">
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-100">
                  <span className="text-3xl text-amber-500">?</span>
                </div>
                <h3 className="text-xl font-bold text-[hsl(var(--foreground))] text-center mb-2">Budget Overrun Warning</h3>
                <p className="text-[hsl(var(--muted-foreground))] text-center text-sm leading-relaxed mb-6">
                  The estimated cost for this RFQ (<strong>{formData.currency || 'USD'} {parseFloat(formData.estimatedTotalCost).toLocaleString()}</strong>)
                  exceeds the estimated budget limit for this RFQ.
                  <br /><br />
                  You can still publish. Over-budget quotations will require vessel owner or superior approval before finalize.
                  <br /><br />
                  Do you still want to proceed with sending this RFQ?
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsBudgetWarningOpen(false);
                      setPendingPublishAfterBudget(false);
                    }}
                    className="flex-1 px-4 py-2.5 border border-[#0088cc]/20 text-[hsl(var(--foreground))] font-bold rounded hover:bg-[#0088cc]/5 transition-colors uppercase text-xs"
                  >
                    Cancel & Review
                  </button>
                  <button
                    onClick={() => {
                      setIsBudgetWarningOpen(false);
                      if (pendingPublishAfterBudget) {
                        setPendingPublishAfterBudget(false);
                        executeSubmit('sent');
                      }
                    }}
                    className="flex-1 px-4 py-2.5 bg-[#0088cc] text-white font-bold rounded hover:bg-[#0077b3] transition-colors uppercase text-xs"
                  >
                    Proceed Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default CreateRFQPage;
